import { logger } from '@/utils/logger';
import { PolybankWordmark } from '@/components/shared/PolybankLogo';
import appServices from '@/services/appServices';
import { AppUser, UserSettings } from '@polybank/shared';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface AuthContextType {
  // Auth state
  user: AppUser | undefined;
  userProfile: UserSettings | undefined;
  isAuthenticated: boolean;
  isGuest: boolean;
  isLoading: boolean;
  error: string | undefined;

  // Auth actions
  signUp: (email: string, password: string, name: string) => Promise<AppUser>;
  signIn: (email: string, password: string) => Promise<AppUser>;
  signOut: () => Promise<void>;
  createGuestSession: () => Promise<void>;
  upgradeGuest: (
    email: string,
    password: string,
    name: string
  ) => Promise<void>;
  signInAndMergeData: (email: string, password: string) => Promise<AppUser>;
  updateProfile: (updates: Partial<UserSettings>) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Initialize with current user synchronously to avoid race conditions with router
  const [user, setUser] = useState<AppUser | undefined>(() => {
    // Try to get current user synchronously first
    const currentUser = appServices.auth.getCurrentUser();
    logger.debug('AuthProvider initial state - currentUser:', currentUser);
    return currentUser;
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      logger.debug('Auth Context: Starting initialization...');

      await appServices.auth.initialize();
      try {
        setIsLoading(true);
        setError(undefined);

        const currentUser = appServices.auth.getCurrentUser();

        if (currentUser) {
          logger.debug('Auth Context: User loaded successfully');
          setUser(currentUser);
        } else {
          logger.debug('Auth Context: No authenticated user found');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        logger.error('Auth Context: Failed to initialize:', err);
        const errorMessage =
          err.message || 'Failed to initialize authentication';
        setError(errorMessage);

        // If it's a timeout or connection error, don't show it as a blocking error
        if (
          errorMessage.includes('timeout') ||
          errorMessage.includes('network')
        ) {
          setError(undefined); // Let user try to login manually
        }
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
        logger.debug('Auth Context: Initialization complete');
      }
    };
    initializeAuth();
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, name: string) => {
      try {
        setIsLoading(true);
        setError(undefined);

        const newUser = await appServices.auth.createAccount(
          email,
          password,
          name
        );
        setUser(newUser);
        return newUser;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        logger.error('Sign up failed:', err);
        setError(err.message || 'Failed to create account');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(undefined);

      const loggedInUser = await appServices.auth.loginWithEmailPassword(
        email,
        password
      );
      setUser(loggedInUser);
      return loggedInUser;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      logger.error('Sign in failed:', err);
      setError(err.message || 'Failed to sign in');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      await appServices.auth.logout();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      logger.error('Sign out failed:', err);
      setError(err.message || 'Failed to sign out');
    } finally {
      setUser(undefined);
      setError(undefined);
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(
    async (
      updates: Partial<UserSettings & { name: string }>
    ): Promise<void> => {
      try {
        setError(undefined);
        await appServices.auth.updatePreferences(updates);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        logger.error('Update profile failed:', err);
        setError(err.message || 'Failed to update profile');
        throw err;
      }
    },
    []
  );

  const completeOnboarding = useCallback(async (): Promise<void> => {
    try {
      await appServices.auth.completeOnboarding();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      logger.error('Complete onboarding failed:', err);
      setError(err.message || 'Failed to complete onboarding');
      throw err;
    }
  }, []);

  const createGuestSession = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(undefined);
      const guestUser = await appServices.auth.createGuestSession();
      setUser(guestUser);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      logger.error('Guest session creation failed:', err);

      // Handle rate limiting errors more gracefully
      if (
        err.message?.includes('Rate limit') ||
        err.message?.includes('wait')
      ) {
        logger.debug('Rate limited - will retry automatically later');
        // Don't set as hard error for rate limits, just log it
        setError(undefined);
      } else {
        setError(err.message || 'Failed to create guest session');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const upgradeGuest = useCallback(
    async (email: string, password: string, name: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(undefined);

        const upgradedUser = await appServices.auth.upgradeGuestToUser(
          email,
          password,
          name
        );
        setUser(upgradedUser);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        logger.error('Guest upgrade failed:', err);
        setError(err.message || 'Failed to upgrade guest account');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const signInAndMergeData = useCallback(
    async (email: string, password: string): Promise<AppUser> => {
      try {
        setIsLoading(true);
        setError(undefined);

        const mergedUser = await appServices.auth.signInAndMergeGuestData(
          email,
          password
        );
        setUser(mergedUser);
        return mergedUser;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        logger.error('Sign in and merge failed:', err);
        setError(err.message || 'Failed to sign in and merge data');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const clearError = useCallback((): void => {
    setError(undefined);
  }, []);

  // Memoize computed values
  const isAuthenticated = useMemo(() => {
    const result = !!user;
    logger.debug(
      'AuthProvider isAuthenticated computed:',
      result,
      'user:',
      !!user
    );
    return result;
  }, [user]);

  const isGuest = useMemo(
    () => !!isAuthenticated && !user?.email,
    [user, isAuthenticated]
  );

  const value: AuthContextType = useMemo(() => {
    return {
      // State
      user,
      userProfile: user?.prefs,
      isAuthenticated,
      isGuest,
      isLoading,
      error,

      // Actions
      signUp,
      signIn,
      signOut,
      createGuestSession,
      upgradeGuest,
      signInAndMergeData,
      updateProfile,
      completeOnboarding,
      clearError,
    };
  }, [
    user,
    isAuthenticated,
    isGuest,
    isLoading,
    error,
    signUp,
    signIn,
    signOut,
    createGuestSession,
    upgradeGuest,
    signInAndMergeData,
    updateProfile,
    completeOnboarding,
    clearError,
  ]);

  if (!isInitialized) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <PolybankWordmark logoSize='xl' className='flex-col' />
      </div>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Alternative hooks that match Jazz's API for easier migration
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

export function useAccount() {
  const { user, userProfile, signOut } = useAuth();

  return useMemo(
    () => ({
      me: userProfile,
      agent: user,
      logOut: signOut,
    }),
    [user, userProfile, signOut]
  );
}
